/**
 * Pure redirect decision logic — no React, no hooks, no side effects.
 * Given progress state and current path, returns the path to redirect to,
 * or null if no redirect is needed.
 */

export function getRedirectTarget(
  progress: any,
  signup: any,
  currentPath: string
): string | null {
  const isProfileComplete = signup?.profile_completed ?? false;
  const isDocumentsComplete = signup?.documents_complete ?? false;

  // ── 1. Profile incomplete → signup stage ──
  if (!isProfileComplete || !isDocumentsComplete) {
    const target = getSignupTarget(signup?.current_stage);
    return target !== currentPath ? target : null;
  }

  // ── From here, profile is complete ──

  // Find active request by priority (skip rejected)
  const active = progress.active_requests?.filter(
    (r: any) => r.status !== 'rejected'
  ) || [];
  const activeRequest =
    active.find((r: any) => r.request_type === 'registration') ||
    active.find((r: any) => r.request_type === 'closure') ||
    active.find((r: any) => r.request_type === 'onboarding') ||
    active[0];

  // ── 2. Changes requested → /corrections ──
  if (activeRequest?.status === 'changes_requested' &&
      (activeRequest.corrections?.corrections?.length ?? 0) > 0) {
    return currentPath !== '/corrections' ? '/corrections' : null;
  }

  // ── 3. Active request → that request's stage route ──
  if (activeRequest) {
    const target = getRequestTarget(activeRequest);
    if (target && target !== currentPath) {
      // For non-actionable stages (target = /requests), allow request detail pages too
      if (target === '/requests' && (
        currentPath === '/success' ||
        currentPath.startsWith('/request/') ||
        currentPath.startsWith('/closure-request/')
      )) {
        return null;
      }
      return target;
    }
    return null;
  }

  // ── 4. Has clinic → /clinic-management ──
  if (progress.clinic_id) {
    const clinicPages = [
      '/clinic-management', '/requests', '/clinic', '/corrections', '/success',
      '/clinic-closure', '/existing-clinic-closure',
      '/closure-documents', '/closure-review', '/closure-agree-terms', '/payment',
    ];
    const isOnClinicPage = clinicPages.includes(currentPath)
      || currentPath.startsWith('/request/')
      || currentPath.startsWith('/closure-request/');

    return !isOnClinicPage ? '/clinic-management' : null;
  }

  // ── 5. No clinic, no requests → /home ──
  const flowStartPages = ['/existing-clinic-closure', '/clinic_info_registration'];
  if (currentPath !== '/home' && !flowStartPages.includes(currentPath)) {
    return '/home';
  }

  return null;
}

// ── Where to send based on signup stage ──
function getSignupTarget(currentStage?: string | null): string {
  switch (currentStage) {
    case 'profile':    return '/doctor_info_registration';
    case 'documents':  return '/doctor_documents';
    case 'completed':  return '/home';
    default:           return '/doctor_info_registration';
  }
}

// ── Where to send based on request type + stage ──
function getRequestTarget(request: { request_type: string; current_stage: string }): string | null {
  const { request_type, current_stage } = request;

  if (request_type === 'registration') {
    switch (current_stage) {
      case 'request_info':  return '/clinic_info_registration';
      case 'documents':     return '/clinic_photo';
      case 'agree_terms':   return '/agree_terms';
      case 'payment':       return '/payment';
      default:              return '/requests';
    }
  }

  if (request_type === 'closure') {
    switch (current_stage) {
      case 'documents':     return '/closure-documents';
      case 'agree_terms':   return '/closure-agree-terms';
      case 'payment':       return '/payment';
      default:              return '/requests';
    }
  }

  if (request_type === 'onboarding') {
    switch (current_stage) {
      case 'request_info':  return '/clinic_info_registration';
      case 'documents':     return '/onboarding_photo';
      default:              return '/requests';
    }
  }

  return null;
}
